import React, { useEffect, useState, useCallback, useMemo, useRef } from "react";
import type { SiteManifest, PublishedPage, ShareSnapshot } from "./types";
import {
  fetchManifest,
  fetchPage,
  fetchShareSnapshot,
  getSubdomain,
  UnauthorizedError,
} from "./api";
import { SiteShell } from "./components/SiteShell";
import { PageView } from "./components/PageView";
import { Homepage } from "./components/Homepage";
import { HomepageLandingView } from "./components/HomepageLandingView";
import { ShareView } from "./components/ShareView";
import { NotFound } from "./components/NotFound";
import { Loading } from "./components/Loading";
import { SignIn, SignInVerify } from "./components/SignIn";
import { LiveChatWidget } from "./components/LiveChatWidget";
import {
  buildFolderPathMaps,
  folderUrlPath,
  getFolderPathFromPathname,
  resolveFolderUrlPath,
  resolveLegacyFolderToken,
} from "./lib/folderSlug";

type RouteMode =
  | { mode: "site" }
  | { mode: "share"; token: string }
  | { mode: "signin" }
  | { mode: "signin-verify"; token: string };

function resolveMode(): RouteMode {
  const path = window.location.pathname;
  if (path.startsWith("/s/")) {
    return { mode: "share", token: path.replace("/s/", "").split("/")[0] };
  }
  if (path === "/sign-in" || path === "/sign-in/") {
    return { mode: "signin" };
  }
  if (path.startsWith("/sign-in/verify")) {
    const search = new URLSearchParams(window.location.search);
    return { mode: "signin-verify", token: search.get("token") ?? "" };
  }
  return { mode: "site" };
}

function redirectToSignIn(): void {
  const current =
    window.location.pathname + window.location.search + window.location.hash;
  const redirect = encodeURIComponent(current === "/sign-in" ? "/" : current);
  window.location.replace(`/sign-in?redirect_to=${redirect}`);
}

/**
 * The site renders one of three views, derived entirely from the pathname so
 * deep links, refreshes, and the browser back/forward buttons all resolve to
 * the same state:
 *   `/`              → home (folder index / homepage)
 *   `/f/<a>/<b>/…`   → a folder, identified by its nested public path
 *   `/<article-slug>`→ a published article
 */
type SiteView =
  | { kind: "home" }
  | { kind: "folder"; path: string }
  | { kind: "page"; slug: string };

function getViewFromPath(): SiteView {
  const pathname = decodeURIComponent(window.location.pathname);
  if (pathname === "/" || pathname === "") return { kind: "home" };

  const folderPath = getFolderPathFromPathname(pathname);
  if (folderPath) return { kind: "folder", path: folderPath };
  // Bare `/f` or `/f/` with no folder selected falls back to the index.
  if (pathname === "/f" || pathname === "/f/") return { kind: "home" };

  const slug = pathname.replace(/^\//, "");
  if (!slug || slug.startsWith("s/")) return { kind: "home" };
  return { kind: "page", slug };
}

export const Router: React.FC = () => {
  const [mode, setMode] = useState(() => resolveMode());

  useEffect(() => {
    const handler = () => setMode(resolveMode());
    window.addEventListener("popstate", handler);
    return () => window.removeEventListener("popstate", handler);
  }, []);

  if (mode.mode === "share") {
    return <ShareRoute token={mode.token} />;
  }
  if (mode.mode === "signin") {
    const search = new URLSearchParams(window.location.search);
    return <SignIn redirectTo={search.get("redirect_to")} />;
  }
  if (mode.mode === "signin-verify") {
    if (!mode.token) return <NotFound message="Missing sign-in token." />;
    return <SignInVerify token={mode.token} />;
  }

  return <SiteRoute />;
};

const SiteRoute: React.FC = () => {
  const [manifest, setManifest] = useState<SiteManifest | null>(null);
  const [currentPage, setCurrentPage] = useState<PublishedPage | null>(null);
  const [view, setView] = useState<SiteView>(() => getViewFromPath());
  const [loading, setLoading] = useState(true);
  const [pageLoading, setPageLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const subdomain = useMemo(getSubdomain, []);
  const reqIdRef = useRef(0);

  const folderPathMaps = useMemo(
    () => buildFolderPathMaps(manifest?.folders ?? []),
    [manifest?.folders]
  );

  useEffect(() => {
    if (!subdomain) {
      setError("No subdomain detected.");
      setLoading(false);
      return;
    }

    fetchManifest()
      .then((m) => setManifest(m))
      .catch((err) => {
        if (err instanceof UnauthorizedError) {
          redirectToSignIn();
          return;
        }
        setError("Site not found.");
      })
      .finally(() => setLoading(false));
  }, [subdomain]);

  /**
   * Redirect legacy `#folder-<uuid|slug>` share links to the canonical
   * `/f/<path>` URL once the manifest (and thus the path maps) is available.
   * Uses `replaceState` so the old hash URL doesn't linger in history.
   */
  useEffect(() => {
    if (!manifest) return;
    const hash = window.location.hash;
    if (!hash.startsWith("#folder-")) return;
    const uuid = resolveLegacyFolderToken(hash, folderPathMaps, manifest.folders);
    if (uuid) {
      const path = folderUrlPath(folderPathMaps, uuid);
      window.history.replaceState(null, "", `/f/${path}`);
      setView({ kind: "folder", path });
    } else {
      window.history.replaceState(null, "", window.location.pathname);
    }
  }, [manifest, folderPathMaps]);

  useEffect(() => {
    if (!manifest) return;

    if (view.kind !== "page") {
      setCurrentPage(null);
      setPageLoading(false);
      return;
    }

    const reqId = ++reqIdRef.current;
    setPageLoading(true);
    fetchPage(view.slug)
      .then((page) => {
        if (reqId === reqIdRef.current) setCurrentPage(page);
      })
      .catch((err) => {
        if (err instanceof UnauthorizedError) {
          redirectToSignIn();
          return;
        }
        if (reqId === reqIdRef.current) setCurrentPage(null);
      })
      .finally(() => {
        if (reqId === reqIdRef.current) setPageLoading(false);
      });
  }, [manifest, view]);

  useEffect(() => {
    const handler = () => {
      setView(getViewFromPath());
    };
    window.addEventListener("popstate", handler);
    return () => window.removeEventListener("popstate", handler);
  }, []);

  const navigateTo = useCallback(
    (slug: string | null, anchor?: string | null) => {
      const hash = anchor ? `#${anchor}` : "";
      const nextPath = slug ? `/${slug}${hash}` : `/${hash}`;
      const currentFull =
        window.location.pathname + window.location.hash;
      if (currentFull !== nextPath) {
        window.history.pushState(null, "", nextPath);
      }
      setView(slug ? { kind: "page", slug } : { kind: "home" });

      if (anchor) {
        // Wait for the page to render, then scroll to the heading.
        let tries = 0;
        const tryScroll = () => {
          const el = document.getElementById(anchor);
          if (el) {
            el.scrollIntoView({ behavior: "smooth", block: "start" });
            return;
          }
          if (tries++ < 20) setTimeout(tryScroll, 75);
        };
        setTimeout(tryScroll, 50);
      } else {
        window.scrollTo({ top: 0, behavior: "auto" });
      }
    },
    []
  );

  const navigateToFolder = useCallback(
    (folderId: string | null) => {
      const nextPath = folderId
        ? `/f/${folderUrlPath(folderPathMaps, folderId)}`
        : "/";
      if (window.location.pathname !== nextPath) {
        window.history.pushState(null, "", nextPath);
      }
      setView(
        folderId
          ? { kind: "folder", path: folderUrlPath(folderPathMaps, folderId) }
          : { kind: "home" }
      );
      window.scrollTo({ top: 0, behavior: "auto" });
    },
    [folderPathMaps]
  );

  if (loading) return <Loading />;
  if (error || !manifest) return <NotFound message={error || "Site not found."} />;

  const isHome = view.kind === "home";
  const isFolder = view.kind === "folder";
  const activeFolderId = isFolder
    ? resolveFolderUrlPath(folderPathMaps, view.path)
    : null;

  // Render the live chat widget at the route level (not inside HomepageLandingView
  // or SiteShell). If it were inside those components, navigating between the
  // homepage and a page would unmount one instance and mount another — the
  // widget's `<script>` tag gets re-injected and re-executes on top of the
  // iframe/globals the first load already installed, which corrupts its state
  // so subsequent clicks don't open the panel. Mounting here keeps it stable
  // across the homepage↔page transition.
  const chat =
    manifest.space.liveChatScriptUrl && manifest.space.liveChatAppKey ? (
      <LiveChatWidget
        scriptUrl={manifest.space.liveChatScriptUrl}
        appKey={manifest.space.liveChatAppKey}
      />
    ) : null;

  // A `/f/<path>` that doesn't map to any folder (renamed/removed) is a 404.
  if (isFolder && !activeFolderId) {
    return (
      <>
        <NotFound message="Folder not found." />
        {chat}
      </>
    );
  }

  const heroHomepage =
    manifest.space.layout.showHomepage && (isHome || isFolder);

  if (heroHomepage) {
    return (
      <>
        <HomepageLandingView
          manifest={manifest}
          onNavigate={navigateTo}
          activeFolderId={activeFolderId}
          onSelectFolder={navigateToFolder}
        />
        {chat}
      </>
    );
  }

  return (
    <>
      <SiteShell
        manifest={manifest}
        currentPage={currentPage}
        onNavigate={navigateTo}
        onNavigateToFolder={navigateToFolder}
      >
        {pageLoading ? (
          <Loading />
        ) : isHome || isFolder ? (
          <Homepage
            manifest={manifest}
            onNavigate={navigateTo}
            scrollToFolderId={activeFolderId}
          />
        ) : currentPage ? (
          <PageView page={currentPage} manifest={manifest} onNavigate={navigateTo} />
        ) : (
          <NotFound message="Page not found." />
        )}
      </SiteShell>
      {chat}
    </>
  );
};

const ShareRoute: React.FC<{ token: string }> = ({ token }) => {
  const [snapshot, setSnapshot] = useState<ShareSnapshot | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchShareSnapshot(token)
      .then(setSnapshot)
      .catch(() => setError("This share link is invalid, expired, or has been revoked."))
      .finally(() => setLoading(false));
  }, [token]);

  if (loading) return <Loading />;
  if (error || !snapshot) return <NotFound message={error || "Share link not found."} />;

  return <ShareView snapshot={snapshot} />;
};
